import { Mutation, Action, VuexModule, Module } from "vuex-module-decorators";
import { Account, AuthorizationModel, CredentialsModel, Role, LoginByFacilityResponse } from "@/commons/services/Authorization";
import { FacilityGetModel, FacilityItemType } from "@/commons/services/Facilities";

/** store state model */
export interface LoginUserState {
  /** ログイン状態 */
  loggedIn: boolean;
  /** 有料プラン広告表示状態 */
  showedPaidPlanAds: boolean;
  /** 面会予約リマインド実施状態 */
  reservationsReminded: boolean;
  /** 外出外泊届けリマインド実施状態 */
  outingsReminded: boolean;
  /** お知らせ未読リマインド実施状態 */
  unreadNotificationsReminded: boolean;
  /** ログインユーザー情報 */
  loginUser: Account;
  /** 複数施設ログインのためにパスワードを保持する */
  password: string;
  /** 施設 */
  facility: FacilityGetModel;
  /** ログイン可能施設リスト */
  facilities: Array<FacilityGetModel>;
  /** cognito認証情報 */
  authorization: AuthorizationModel;
  /** S3認証情報 */
  credentials: CredentialsModel;
}

/**
 * ### LoginUser store
 *
 * - store
 *   - ログイン状態を保持する
 *   - ログインしたユーザーを保持する
 * - action, mutation
 *   - ログインを行う
 *   - ログアウトを行う
 */
@Module({ name: "loginUser" })
export default class LoginUser extends VuexModule implements LoginUserState {
  loggedIn: boolean = this.initailizeloggedIn();
  showedPaidPlanAds: boolean = this.initailizeShowedPaidPlanAds();
  reservationsReminded: boolean = this.initailizeReminded();
  outingsReminded: boolean = this.initailizeReminded();
  unreadNotificationsReminded: boolean = this.initailizeReminded();
  loginUser: Account = this.initailizeLoginUser();
  password: string = "";
  facility: FacilityGetModel = this.initailizeFacilityModel();
  facilities: Array<FacilityGetModel> = [];
  authorization: AuthorizationModel = this.initailizeAuthorization();
  credentials: CredentialsModel = this.initailizeCredentials();

  /** ログイン状態の初期化 */
  private initailizeloggedIn(): boolean {
    return false;
  }

  /** 有料プラン広告表示状態の初期化 */
  private initailizeShowedPaidPlanAds(): boolean {
    return false;
  }

  /** リマインド状態の初期化 */
  private initailizeReminded(): boolean {
    return false;
  }

  /** ログインユーザーの初期化 */
  private initailizeLoginUser(): Account {
    return {
      userId: "",
      facilityId: 0,
      name: "",
      kana: "",
      tel: "",
      mail: "",
      confirmedNotificationIds: [],
      latestRequestDatetime: 0,
      receiveNotification: true,
      started: 0,
      deleteFlag: false,
      created: 0,
      updated: 0,
      permissions: {
        functions: {
          /** 家族連絡サービス */
          facilitiesFunction: false,
          roomsFunction: false,
          useFamilyFunctionsFunction: false,
          individuallyUseFamilyFunctionsFunction: false,
          residentsFunction: false,
          familiesFunction: false,
          otherUserGroupsFunction: false,
          useOtherUserFunctionsFunction: false,
          sendMessageGroupsFunction: false,
          careimagesFunction: false,
          picturesFunction: false,
          eventsFunction: false,
          reservationsFunction: false,
          outingsFunction: false,
          sendMessagesFunction: false,
          sendDocumentsFunction: false,
          receiveMessagesFunction: false,
          familyMessagesFunction: false,
          reportsFunction: false,
          safetyConfirmationFunction: false,
          roomSchedulesFunction: false,
          /** 待機者サービス */
          useWaiterService: false,
          waitersFunction: false,
          waiterSendMessagesFunction: false,
          waiterFollowersFunction: false,
          inspectionReservationsFunction: false,
          recruitmentContactsFunction: false,
          questionnairesFunction: false,
          waiterReceiveMessagesFunction: false,
          /** 他事業所連携 */
          useOtherOfficeConnectionService: false,
          otherOfficeTopPageFunction: false,
          otherOfficeMessagesFunction: false,
          otherOfficeOnlineMeetingsFunction: false,
          otherOfficeRoomSchedulesFunction: false
        },
        sendMessagesFunction: {
          useBasicSetting: false,
          useSaveDraftMessage: false,
          useSendAndReply: false,
          useSendTargetToAllFamily: false,
          useSendTargetToFamily: false,
          useSendTargetToFamilySendMessageGroup: false,
          useSendTargetToOtherUser: false,
          useSendTargetToOtherUserSendMessageGroup: false,
          useSendTargetToResidentGroup: false,
          useSendTargetToOtherUserGroup: false,
          useTemplate: false,
          useSendTargetToAllWaiter: false,
          useSendTargetToWaiter: false
        },
        receiveMessagesFunction: {
          showFamilyMessages: false,
          showOtherUserMessages: false,
          useFamilyReply: false,
          useOtherUserReply: false
        },
        reservationsFunction: {
          useReservationSettings: false,
          useReservationSchedules: false,
          useOnlineReservationSettings: false,
          useOnlineReservationSchedules: false,
          useReserveInstead: false,
          useEditReservations: false,
          useStartOnlineReservations: false
        },
        waiterReceiveMessagesFunction: {
          showMessages: false,
          useReply: false
        },
        sendDocumentsFunction: {
          useEdit: false,
          useTemplate: false,
          useDraft: false,
          useControl: false,
          showTargetToAllFamily: false,
          showTargetToFamily: false,
          showTargetToOtherUser: false,
          showTargetToFamilySendMessageGroup: false,
          showTargetToOtherUserSendMessageGroup: false,
          showTargetToResidentGroup: false,
          showTargetToOtherUserGroup: false
        },
        residentGroups: [],
        otherUserGroups: [],
        showMessages: 0
      },
      role: Role.STANDARD_USER
    };
  }
  /** 施設情報の初期化 */
  private initailizeFacilityModel(): FacilityGetModel {
    return {
      id: 0,
      address: "",
      created: 0,
      deleteFlag: false,
      imageUrl: "",
      name: "",
      permittedFunctions: {
        careimage: false,
        opinion: false,
        reservation: false,
        onlineReservation: false,
        outing: false,
        picture: false,
        event: false,
        otherUserOpinion: false,
        waiterOpinion: false
      },
      defaultFamilyPermittedFunctions: {
        opinion: false,
        picture: false
      },
      notifications: {
        event: [],
        family: [],
        message: [],
        messageShowText: false,
        picture: [],
        reservation: [],
        outing: [],
        otherUser: [],
        otherUserMessage: [],
        otherUserMessageShowText: false,
        otherOfficeMessage: [],
        otherOfficeMessageShowText: false,
        otherOfficeOnlineMeeting: []
      },
      messageSettings: {
        replyLimit: 0
      },
      planType: -1,
      plan: -1,
      waiterServicePlanType: -1,
      tel: "",
      url: "",
      validPeriodFrom: 0,
      validPeriodTo: 0,
      waiterServiceValidPeriodTo: 0,
      waiterServiceValidPeriodFrom: 0,
      updated: 0,
      residentTransferableFacilities: [],
      otherUserTransferableFacilities: [],
      waiterViewableFacilities: [],
      useAdmissionApplicationTemplateId: null,
      useAdmissionJudgementTemplateId: null,
      useFamilyService: false,
      useWaiterService: false,
      recruitmentRoom: {
        multiBed: false,
        conventinalSingle: false,
        unitTypeSingle: false,
        double: false,
        single: false
      },
      customRecruitmentRoom: [],
      receiveMessageGuidanceToFamilies: "",
      receiveMessageGuidanceToOtherUserGroups: "",
      receiveMessageGuidanceToWaiters: "",
      cooperationSystems: {
        careTree: false,
        hitomeQTree: false,
        hitomeQCareSupport: false,
        iMedic: false,
        scopReceipt: false
      },
      careOfficeNumbers: [],
      useOtherOfficeConnectionService: false,
      hitomeQCareSupportFacilityIds: []
    };
  }

  /** cognito認証情報の初期化 */
  private initailizeAuthorization(): AuthorizationModel {
    return {
      accessToken: "",
      refreshToken: "",
      expiration: "1990-1-1T00:00:00"
    };
  }

  /** S3認証情報の初期化 */
  private initailizeCredentials(): CredentialsModel {
    return {
      accessKeyId: "",
      secretAccessKey: "",
      expiration: "1990-1-1T00:00:00"
    };
  }

  /** ユーザーのgetters */
  get user(): Account {
    return this.loginUser;
  }

  /** ユーザー名のgetters */
  get userName(): string {
    return this.loginUser.name;
  }

  /** 施設のgetters */
  get getFacility(): FacilityGetModel {
    return this.facility;
  }

  @Mutation
  private LOGIN(payload: { loginByFacilityResponse: LoginByFacilityResponse; facilities: Array<FacilityGetModel>; password: string }): void {
    this.loggedIn = true;
    this.loginUser = payload.loginByFacilityResponse.account;
    this.password = payload.password;
    this.facility = payload.loginByFacilityResponse.facility;
    this.authorization = payload.loginByFacilityResponse.authorization;
    this.credentials = payload.loginByFacilityResponse.credentials;
    this.facilities = payload.facilities;
    return;
  }

  @Mutation
  private LOGOUT(): void {
    this.loggedIn = false;
    this.showedPaidPlanAds = false;
    this.reservationsReminded = false;
    this.outingsReminded = false;
    this.unreadNotificationsReminded = false;
    this.facilities = [];
    return;
  }

  @Mutation
  private SET_LOGINUSER(user: Account) {
    this.loginUser = user;
    return;
  }

  @Mutation
  private SET_FACILITY(facility: FacilityGetModel) {
    this.facility = facility;
    return;
  }

  @Mutation
  private SET_AUTHORIZATION(authorization: AuthorizationModel) {
    this.authorization = authorization;
    return;
  }

  @Mutation
  private SET_CREDENTIALS(credentials: CredentialsModel) {
    this.credentials = credentials;
    return;
  }

  @Mutation
  private SET_SHOWEDPAIDPLANADS(showedPaidPlanAds: boolean) {
    this.showedPaidPlanAds = showedPaidPlanAds;
    return;
  }

  @Mutation
  private SET_RESERVATIONS_REMINDED(reminded: boolean) {
    this.reservationsReminded = reminded;
    return;
  }

  @Mutation
  private SET_OUTINGS_REMINDED(reminded: boolean) {
    this.outingsReminded = reminded;
    return;
  }

  @Mutation
  private SET_UNREAD_NOTIFICATIONS_REMINDED(reminded: boolean) {
    this.unreadNotificationsReminded = reminded;
    return;
  }

  /**
   * ### ログインする
   *
   * - 渡されたserviceクラスでログイン処理を実施する
   * - ログインが成功(例外発生無し)であれば、Cookieを更新する
   * - { rawError: true }を設定しないと例外発生時に正しくエラーが上位に投げられない
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public async login(payload: { loginByFacilityResponse: LoginByFacilityResponse; facilities: Array<FacilityGetModel>; password: string }): Promise<Account> {
    this.LOGIN(payload);
    return payload.loginByFacilityResponse.account;
  }

  /**
   * ### ログアウトする
   */
  @Action({ rawError: true })
  public logout(): void {
    this.LOGOUT();
    return;
  }

  /**
   * ### ユーザー情報を更新する
   *
   * - 引数で渡されたユーザー情報でstateのユーザー情報を更新する
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateLoginUser(payload: { loginUser: Account }): void {
    this.SET_LOGINUSER(payload.loginUser);
    return;
  }

  /**
   * ### 施設情報を更新する
   *
   * - 引数で渡された施設情報でstateの施設情報を更新する
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateFacility(payload: { facility: FacilityGetModel }): void {
    this.SET_FACILITY(payload.facility);
    return;
  }

  /**
   * ### cognito認証情報を更新する
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateAuthorization(payload: { authorization: AuthorizationModel }): void {
    this.SET_AUTHORIZATION(payload.authorization);
    return;
  }

  /**
   * ### S3認証情報を更新する
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateCredentials(payload: { credentials: CredentialsModel }): void {
    this.SET_CREDENTIALS(payload.credentials);
    return;
  }

  /**
   * ### 有料プラン広告表示状態
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateShowedPaidPlanAds(payload: boolean): void {
    this.SET_SHOWEDPAIDPLANADS(payload);
    return;
  }

  /**
   * ### 面会予約リマインド情報を更新する
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateReservationsReminded(payload: boolean): void {
    this.SET_RESERVATIONS_REMINDED(payload);
    return;
  }

  /**
   * ### 外出外泊届けリマインド情報を更新する
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateOutingsReminded(payload: boolean): void {
    this.SET_OUTINGS_REMINDED(payload);
    return;
  }

  /**
   * ### お知らせ未読リマインド情報を更新する
   *
   * @param payload Actionは第一引数でしか値を渡せないので引数をまとめたオブジェクト
   */
  @Action({ rawError: true })
  public updateUnreadNotificationsReminded(payload: boolean): void {
    this.SET_UNREAD_NOTIFICATIONS_REMINDED(payload);
    return;
  }
}
